import { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import OrderList from "@/components/orders/OrderList";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProjectsDone() {
  const navigate = useNavigate();
  const [deliveredOrders, setDeliveredOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [workflows, setWorkflows] = useState({ sublimation: [], embroidery: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Filter orders based on search term
    if (!searchTerm.trim()) {
      setFilteredOrders(deliveredOrders);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = deliveredOrders.filter(order => 
        order.client_name?.toLowerCase().includes(term) ||
        order.product_type?.toLowerCase().includes(term) ||
        order.printing_method?.toLowerCase().includes(term) ||
        order.id?.toLowerCase().includes(term) ||
        order.production_notes?.toLowerCase().includes(term)
      );
      setFilteredOrders(filtered);
    }
  }, [searchTerm, deliveredOrders]);

  const fetchData = async () => {
    try {
      const [ordersResponse, settingsResponse] = await Promise.all([
        axios.get(`${API}/orders`),
        axios.get(`${API}/settings/minimal`)
      ]);
      const delivered = ordersResponse.data.filter(order => order.current_status === "Delivered");
      setDeliveredOrders(delivered);
      setFilteredOrders(delivered);
      setWorkflows({
        sublimation: settingsResponse.data.sublimation_workflow || [],
        embroidery: settingsResponse.data.embroidery_workflow || []
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    // Refresh list after edit
    fetchData();
  };

  const handleDelete = async (orderId) => {
    try {
      await axios.delete(`${API}/orders/${orderId}`);
      fetchData();
    } catch (error) {
      console.error("Error deleting order:", error);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const order = deliveredOrders.find(o => o.id === orderId);
      
      // Check if moving from Delivered to another status
      if (order && order.current_status === 'Delivered' && newStatus !== 'Delivered') {
        const confirmMove = window.confirm(
          `This will move the order back to "${newStatus}" and it will appear in active orders on the dashboard. Continue?`
        );
        if (!confirmMove) {
          return; // Cancel the status update
        }
      }
      
      await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus });
      toast.success("Order moved back to active status");
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading completed projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div>
              <h1 className="text-3xl font-heading font-extrabold tracking-tight text-primary">
                Projects Done
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Completed and delivered projects ({filteredOrders.length} of {deliveredOrders.length})
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/")} data-testid="back-to-dashboard">
            Back to Dashboard
          </Button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by client name, product type, or printing method..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10"
              data-testid="search-projects"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-sm text-muted-foreground mt-2">
              Found {filteredOrders.length} result{filteredOrders.length !== 1 ? 's' : ''} for &quot;{searchTerm}&quot;
            </p>
          )}
        </div>

        <OrderList
          orders={filteredOrders}
          workflows={workflows}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onStatusUpdate={handleStatusUpdate}
        />

        {filteredOrders.length === 0 && searchTerm && (
          <div className="bg-card border border-border rounded-md p-12 text-center mt-6">
            <Search className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-heading font-semibold mb-2">No Results Found</h3>
            <p className="text-muted-foreground mb-6">
              No completed projects match &quot;{searchTerm}&quot;
            </p>
            <Button onClick={clearSearch} variant="outline">
              Clear Search
            </Button>
          </div>
        )}

        {deliveredOrders.length === 0 && !searchTerm && (
          <div className="bg-card border border-border rounded-md p-12 text-center mt-6">
            <CheckCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-heading font-semibold mb-2">No Completed Projects Yet</h3>
            <p className="text-muted-foreground mb-6">
              Orders marked as &quot;Delivered&quot; will appear here.
            </p>
            <Button onClick={() => navigate("/")} variant="outline" data-testid="go-to-dashboard">
              Go to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
